import { useState } from 'react';
import { useEditor } from '../../state/store';
import { Snapshot, clearSnapshots, listSnapshots } from '../../state/autosave';
import { Modal } from '../widgets/common';

/**
 * Crash recovery.
 *
 * Shown once at startup when autosave snapshots exist. Restoring opens the
 * snapshot as a new tab rather than replacing anything, so recovery can never
 * destroy work that survived.
 */
export function RecoveryDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [snapshots] = useState<Snapshot[]>(() => listSnapshots());
  const store = useEditor.getState();

  return (
    <Modal
      title="Recover unsaved work"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button
            className="btn"
            onClick={() => {
              clearSnapshots();
              onClose();
            }}
          >
            Discard all
          </button>
          <button className="btn primary" onClick={onClose}>
            Not now
          </button>
        </>
      }
    >
      <p className="dim" style={{ marginTop: 0, lineHeight: 1.5 }}>
        These snapshots were saved automatically. Restoring opens a copy in a new tab; nothing
        currently open is replaced.
      </p>
      {snapshots.map((snapshot, i) => (
        <div className="item" key={i}>
          <span className="name">{snapshot.documentName}</span>
          <span className="tag">{relativeTime(snapshot.savedAt)}</span>
          <button
            className="btn"
            onClick={() => {
              store.loadProject(snapshot.json, snapshot.fileName);
              onClose();
            }}
          >
            Restore
          </button>
        </div>
      ))}
      {snapshots.length === 0 ? <div className="empty-state">No snapshots found.</div> : null}
    </Modal>
  );
}

export function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return 'unknown';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 90) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
